import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Send, Loader2, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, StatCard } from "@/components/PageHeader";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { yearOptions } from "@/lib/format";
import { NativeSelect } from "@/components/Field";

const TZ = "America/Chicago";
const nowMonth = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit" }).format(new Date()).slice(0, 7);
const localDate = (iso) => new Date(iso).toLocaleDateString("en-US", { timeZone: TZ });
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function MonthPicker({ value, onChange }) {
  const [y, m] = value.split("-");
  return (
    <div className="flex gap-2">
      <NativeSelect value={m} onChange={(v) => onChange(`${y}-${v}`)} options={MONTHS.map((n, i) => ({ value: String(i + 1).padStart(2, "0"), label: n }))} testid="missing-month-select" className="w-40" />
      <NativeSelect value={y} onChange={(v) => onChange(`${v}-${m}`)} options={yearOptions()} testid="missing-year-select" className="w-28" />
    </div>
  );
}
const Mark = ({ ok, testid }) => (ok
  ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800" data-testid={testid}><Check className="h-3.5 w-3.5" /> Received</span>
  : <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800" data-testid={testid}><X className="h-3.5 w-3.5" /> Missing</span>);

function ReminderSettings() {
  const { settings, reload } = useApp();
  const cfg = settings.reminders || { enabled: true, day: 25 };
  const [day, setDay] = useState(cfg.day);
  const save = async (patch) => { try { await api.put("/settings", { reminders: patch }); toast.success("Reminder settings saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="reminder-settings">
      <BellRing className="h-6 w-6 text-[hsl(var(--gold))]" />
      <label className="flex items-center gap-3 font-semibold text-slate-800">
        <OnOffSwitch checked={cfg.enabled} onChange={(v) => save({ enabled: v })} testid="reminder-enabled-switch" />
        Automatic monthly email reminders
      </label>
      <form className="flex items-center gap-2 text-sm text-slate-600" onSubmit={(e) => { e.preventDefault(); save({ day: Number(day) }); }}>
        Send on day
        <Input type="number" min="1" max="28" value={day} onChange={(e) => setDay(e.target.value)} className="h-10 w-20" data-testid="reminder-day-input" />
        of each month (9am Central)
        <Button type="submit" variant="outline" size="sm" data-testid="reminder-day-save">Save</Button>
      </form>
      <label className="flex items-center gap-3 text-sm font-semibold text-slate-800">
        <OnOffSwitch checked={cfg.admin_copy !== false} onChange={(v) => save({ admin_copy: v })} testid="reminder-admin-copy-switch" />
        Email Master Admins a monthly summary (same day)
      </label>
      <label className="flex items-center gap-3 text-sm font-semibold text-slate-800">
        <OnOffSwitch checked={cfg.thank_you !== false} onChange={(v) => save({ thank_you: v })} testid="reminder-thank-you-switch" />
        Send a thank-you email when a church has sent both reports
      </label>
      <p className="w-full text-xs text-slate-500">Only churches still missing their MWJK tithe or progress report get a reminder, sent to their active church user logins.</p>
    </section>
  );
}

export default function MissingReports() {
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/alerts/missing", { params: { month } }).then((r) => { setData(r.data); setPicked([]); }).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const missing = data?.churches.filter((c) => c.missing) || [];
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/alerts/send-reminders", { month, church_ids: picked.length ? picked : null });
      const d = r.data;
      if (d.failed) toast.error(`${d.emails_sent} sent, ${d.failed} failed. ${d.details.find((x) => x.error)?.error || ""}`);
      else toast.success(`${d.emails_sent} reminder email(s) sent to ${d.churches} church(es)${d.no_email ? ` · ${d.no_email} church(es) have no login email` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Master Admin" title="Missing Reports" subtitle="Churches that have not sent their MWJK tithe or progress report for the month.">
        <MonthPicker value={month} onChange={setMonth} />
        <Button size="lg" onClick={send} disabled={sending || !missing.length} data-testid="send-reminders-btn">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          {picked.length ? `Remind ${picked.length} selected` : "Remind all missing"}
        </Button>
      </PageHeader>
      <ReminderSettings />
      {data && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <StatCard label="Churches missing reports" value={data.missing_count} accent="coral" testid="missing-count" />
          <StatCard label="Missing MWJK tithe" value={data.churches.filter((c) => !c.has_mwjk_tithe).length} accent="gold" testid="missing-tithe-count" />
          <StatCard label="Missing progress report" value={data.churches.filter((c) => !c.has_progress).length} testid="missing-progress-count" />
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="missing-table">
          <thead><tr className="border-b bg-[hsl(var(--sand))] text-left">
            <th className="w-10 px-4 py-3" /><th className="px-4 py-3">Church</th><th className="px-4 py-3">MWJK Tithe</th><th className="px-4 py-3">Progress Report</th>
            <th className="px-4 py-3">Logins</th><th className="px-4 py-3">Last reminder</th>
          </tr></thead>
          <tbody>
            {data && data.churches.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No churches yet.</td></tr>}
            {data?.churches.map((c) => (
              <tr key={c.id} className={`border-b border-slate-100 ${c.missing ? "" : "opacity-60"}`} data-testid={`missing-row-${c.id}`}>
                <td className="px-4 py-3">{c.missing && <Checkbox checked={picked.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`missing-select-${c.id}`} />}</td>
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{c.name}</div><div className="text-xs text-slate-500">{c.state}</div></td>
                <td className="px-4 py-3"><Mark ok={c.has_mwjk_tithe} testid={`missing-tithe-${c.id}`} /></td>
                <td className="px-4 py-3"><Mark ok={c.has_progress} testid={`missing-progress-${c.id}`} /></td>
                <td className="px-4 py-3">{c.user_count || <span className="text-amber-700">None — add a login</span>}</td>
                <td className="px-4 py-3 text-slate-600" data-testid={`missing-last-${c.id}`}>
                  {c.thanked_at ? `Thank-you sent ${localDate(c.thanked_at)}` : c.last_reminder ? `${localDate(c.last_reminder.sent_at)} · ${c.last_reminder.status.replace("_", " ")} (${c.last_reminder.trigger})` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
